import { IUser } from '../interfaces/user.interface';
import { UserRole } from '../types/user.type';

/**
 * Single source of truth for deriving a user's role identity from their groups.
 *
 * The permission/role system is mid-refactor toward multi-role / union-context
 * (see `.context/permission-system-knowledge.md` §1.3 — the `groups[0]`
 * bottleneck). Keep ALL role-derivation here so the migration touches one file,
 * not the ~11 scattered `user.groups.every(g => g.slug === ...)` checks spread
 * across feature components/directives.
 *
 * WORKFLOW role comes from `group.role`, NOT `group.slug` and NOT the legacy
 * `group.tag[0]`. The slug is the permission-group identity (e.g. the pilot
 * group `rm-pilot`); `role` is the dedicated workflow identity BE now ships
 * (`rm-pilot` carries `role: 'rm'`). Authorization is decided by permission
 * slugs elsewhere — this helper only answers "which workflow role does this
 * user act as", so a permission-cut group must behave as its base role. For
 * every non-pilot group `role === slug`, so this is a no-op there.
 */
export type RoleAwareUser = Pick<IUser, 'groups'> | null | undefined;

/** The user's primary workflow role (first group's `role`, matching `UserModel.role`). */
export const getUserRole = (user: RoleAwareUser): UserRole | undefined =>
  user?.groups?.[0]?.role as UserRole | undefined;

/**
 * True only when EVERY group the user belongs to acts as workflow role `role`.
 * With the current single-group reality this is an exact role match; under
 * multi-role it means "purely this role". This is the one place to revisit when
 * that semantics changes.
 */
export const isUserInRole = (user: RoleAwareUser, role: UserRole): boolean =>
  !!user?.groups?.length && user.groups.every(group => group.role === role);
